import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Self,
  ViewChild
} from '@angular/core';
import { ControlValueAccessor, NgControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { defaultValidationMessages } from './input-text.constants';

@Component({
  selector: 'app-input-text',
  templateUrl: './input-text.component.html',
  styleUrls: ['./input-text.component.scss'],
  standalone: false
})
export class InputTextComponent implements OnInit, AfterViewInit, ControlValueAccessor {
  @ViewChild('control') control!: ElementRef;
  @Input() id = '';
  @Input() label = '';
  @Input() placeholder = '';
  @Input() isClearable = true;
  @Input() hasShowHide = false;
  @Input() type: 'text' | 'password' | 'email' = 'text';
  @Input() autocomplete = '';
  @Input() validationMessages = defaultValidationMessages;
  @Input() ariaDescribedBy = '';
  @Input() tabIndex = 0;
  value = '';
  requiredLabel = false;
  showPassword = false;
  isAutofilled = false;

  @Output() blurred: EventEmitter<void> = new EventEmitter<void>();
  disabled!: boolean;
  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  get isFieldInvalid(): boolean {
    const control = this.controlDir?.control;
    return control?.touched && !control?.valid && !control.disabled;
  }

  get validationTranslationKey(): string {
    return this.getErrorTranslations();
  }

  constructor(
    @Self() public controlDir: NgControl,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    controlDir.valueAccessor = this;
  }

  ngAfterViewInit(): void {
    this.removeClasses();
    this.cdr.detectChanges();
  }

  ngOnInit(): void {
    const control = this.controlDir.control;
    if (control?.errors) {
      this.requiredLabel = Object.keys(control.errors)?.includes('required');
    }
    const validators = control?.validator ? [control.validator, Validators.required] : Validators.required;
    control?.setValidators(validators);
  }

  onFocus(): void {
    this.control.nativeElement.classList.add('form-field--is-active');
  }

  onFocusout(): void {
    this.removeClasses();
  }

  onBlur(): void {
    this.removeClasses();
    this.onTouched();
    this.blurred.emit();
  }

  onAnimationStart(event: AnimationEvent): void {
    if (event.animationName && event.animationName.indexOf('AutoFill') !== -1) {
      this.isAutofilled = true;
    } else {
      this.isAutofilled = false;
    }
  }

  writeValue(value: string): void {
    this.value = value || '';
    this.removeClasses();
  }

  registerOnChange(onChange: (value: any) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouched = onTouched;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
  }

  clearInput(): void {
    const control = this.controlDir.control;
    control.reset();
  }

  getErrorTranslations(): string {
    const control = this.controlDir?.control;
    const errors = control?.errors;

    if (errors) {
      for (const key of Object.keys(errors)) {
        const translationKey = this.validationMessages[key];
        if (translationKey && ['minlength', 'maxlength'].includes(key)) {
          const requiredLength = errors[key]?.requiredLength;
          return this.translateService.instant(translationKey, { number: requiredLength });
        }
        if (translationKey) {
          return this.translateService.instant(translationKey);
        }
      }
    }
    return '';
  }

  togglePasswordVisible(): void {
    if (!this.hasShowHide) {
      return;
    }
    this.showPassword = !this.showPassword;
    this.type === 'password' ? (this.type = 'text') : (this.type = 'password');
  }

  private removeClasses(): void {
    if (!this.control) {
      return;
    }
    this.control.nativeElement.classList.remove('form-field--is-active');
    if (this.value === '') {
      this.control.nativeElement.classList.remove('form-field--is-filled');
    } else {
      this.control.nativeElement.classList.add('form-field--is-filled');
    }
  }
}
